import { notify } from '@affine/component';
import { DocsService } from '@affine/core/modules/doc';
import {
  AccessibleFolderCacheStore,
  FolderStore,
} from '@affine/core/modules/organize';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback } from 'react';

import { WorkbenchService } from '../../../modules/workbench';
import { notifyMovedToTrash } from '../../notify-moved-to-trash';
import { useConnectivityStatus } from '../use-connectivity-status';

interface TrashableDoc {
  id?: string;
  moveToTrash: () => boolean | void;
  restoreFromTrash: () => boolean | void;
}

/**
 * Move a doc to trash and surface a discoverable, recoverable toast.
 *
 * Soft-delete is otherwise silent: the doc lands in the docs trash with no cue.
 * The toast offers Undo (restore the doc) and Open trash (navigate to /trash).
 * Centralized so every doc-delete entry point behaves identically (DRY).
 */
export function useTrashDoc() {
  const t = useI18n();
  const workbench = useService(WorkbenchService).workbench;
  const workspace = useService(WorkspaceService).workspace;
  const docsService = useService(DocsService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const folderStore = useService(FolderStore);
  const connectivityStatus = useConnectivityStatus();
  const isCloudWorkspace = workspace.flavour !== 'local';

  return useCallback(
    async (doc: TrashableDoc) => {
      const docId = typeof doc.id === 'string' ? doc.id : null;
      if (
        isCloudWorkspace &&
        !connectivityStatus.backendAvailable &&
        docId &&
        docsService.isLocallyCreated(docId)
      ) {
        try {
          await docsService.discardLocalDoc(docId);
          accessibleFolderCache.optimisticRemoveDocEverywhere(docId);
          folderStore.removeDocLinksLocalOnly(docId);
          notify.success({ title: t['Deleted']() });
        } catch (error) {
          console.error(`Failed to discard local doc ${docId}:`, error);
          notify.error({ title: 'Failed to delete doc' });
        }
        return;
      }

      doc.moveToTrash();
      if (isCloudWorkspace) return;
      notifyMovedToTrash({
        title: t['com.streamlain.movedToTrash.doc'](),
        undoLabel: t['Undo'](),
        openTrashLabel: t['com.streamlain.movedToTrash.openTrash'](),
        onUndo: () => {
          doc.restoreFromTrash();
        },
        onOpenTrash: () => {
          workbench.open('/trash');
        },
      });
    },
    [
      accessibleFolderCache,
      connectivityStatus.backendAvailable,
      docsService,
      folderStore,
      isCloudWorkspace,
      t,
      workbench,
    ]
  );
}
